import {ChevronLeft, ChevronRight} from "lucide-react";
import React, {useState} from "react";

interface Props {
    totalItems: number;
    onPageChange: (page: number) => void;
    onPerPageChange: (perPage: number) => void;
}

const PER_PAGE_OPTIONS = [5, 10, 20, 50];

export function Pagination({ totalItems, onPageChange, onPerPageChange }: Props) {
    const [currentPage, setCurrentPage] = useState(1);
    const [sessionsPerPage, setSessionsPerPage] = useState(5);

    const totalPages = Math.ceil(totalItems / sessionsPerPage);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        onPageChange(pageNumber);
    };

    const handleSessionsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newPerPage = Number(e.target.value);
        setSessionsPerPage(newPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
        onPerPageChange(newPerPage);
    };

    return (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center">
                <span className="text-sm text-gray-700 mr-2">Show</span>
                <select
                    value={sessionsPerPage}
                    onChange={handleSessionsPerPageChange}
                    className="border border-gray-300 rounded-md text-sm px-2 py-1 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                    {PER_PAGE_OPTIONS.map(number => (
                        <option key={number} value={number}>
                            {number}
                        </option>
                    ))}
                </select>
                <span className="text-sm text-gray-700 ml-2">entries</span>
            </div>

            <div className="flex items-center space-x-2">
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-md ${
                        currentPage === 1
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                    <button
                        key={number}
                        onClick={() => handlePageChange(number)}
                        className={`px-3 py-1 rounded-md text-sm ${
                            currentPage === number
                                ? 'bg-indigo-600 text-white'
                                : 'text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {number}
                    </button>
                ))}

                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-md ${
                        currentPage === totalPages
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>
        </div>
    );
}
